import loadable from '@loadable/component';
import { useCallback, useState } from 'react';
import { useTracking } from 'react-tracking';

import { RouterLink } from '@redux-first-router-link';
import IntersectionObserver from '@researchgate/react-intersection-observer';

import { toSignificant } from '@/utilities/formatting';
import { Grid, Text, Box } from '@simplywallst/ui-core';

import { Snowflake } from '@simplywallst/ui-charts/Snowflake';

import { CompanyIntrinsicDiscount } from '@/components/CompanyIntrinsicDiscount';
import MiniPriceChart, { DeferRender } from '@/components/MiniPriceChart';
import PositiveNegativeValue from '@/components/PositiveNegativeValue';

import { formatterCurrency, formatterPercentage } from '@/utilities/format';

import { useNormalisedCompanySnowflakeScore } from '@/hooks/useNormalisedCompanySnowflakeScore';
import { getTradingItemCurrencyISO } from '@/utilities/getTradingItemCurrencyISO';

import {
  formatCurrencyToScaleTruncated,
  formatCurrencyTruncatedWithSign,
} from '@/utilities/portfolio/formatValue';

import { TableRowCell } from '../../Table';
import { HoldingsTableRow } from './styled';

import type { RowExpandedProps } from './types';

const RematchHoldingRow = loadable(() => import('../../RematchHoldingRow'), {
  resolveComponent: (c) => c.RematchHoldingRow,
});

export const DemoHoldingRowExpanded = ({
  holding,
  onHoldingSelect,
  'data-cy-id': dataCyId,
}: RowExpandedProps) => {
  const { id, company, analysis, currencyISO, units, averagePurchasePrice } =
    holding;

  const lastSharePrice = company.analysisValue?.lastSharePrice ?? null;
  const npvPerShare = company.analysisValue?.npvPerShare ?? null;

  const companyCurrencyISO =
    getTradingItemCurrencyISO(holding.company.data.currencyInfo) ?? 'XXX';

  const [openRematchHoldingRow, setOpenRematchHoldingRow] = useState(false);
  const [isSticky, setIsSticky] = useState(false);

  const { Track, trackEvent } = useTracking({
    modifier: 'expandedRow',
  });

  const points = useNormalisedCompanySnowflakeScore(company.score);

  const handleClick = useCallback(() => {
    onHoldingSelect(id);
    trackEvent({
      action: 'click',
    });
  }, [onHoldingSelect, id, trackEvent]);

  const handleTrackingLink = useCallback(() => {
    trackEvent({
      action: 'click',
      canonicalUrl: company.canonicalURL,
    });
  }, [trackEvent, company.canonicalURL]);

  return (
    <Track>
      {openRematchHoldingRow ? (
        <RematchHoldingRow
          holding={holding}
          onClose={() => setOpenRematchHoldingRow(false)}
        />
      ) : (
        <HoldingsTableRow data-cy-id={dataCyId} tabIndex={0}>
          <IntersectionObserver
            threshold={[1]}
            onChange={({ intersectionRatio }) => {
              setIsSticky(intersectionRatio < 1);
            }}
          >
            <TableRowCell isSticky={isSticky}>
              <Grid
                data-cy-id="company-column"
                templateColumns="48px auto"
                alignItems="center"
                gap="8px"
                paddingRight="x1"
                onClick={handleClick}
              >
                <Snowflake width={48} points={points}>
                  <Snowflake.DotMarkers />
                </Snowflake>
                <Box>
                  <RouterLink
                    data-cy-id="ticker-symbol"
                    noUnderline
                    to={company.canonicalURL}
                    onClick={handleTrackingLink}
                  >
                    <strong>{company.tickerSymbol}</strong>
                  </RouterLink>
                  <Text
                    typography="xSmall"
                    color="whiteRGBA"
                    opacity={0.5}
                    lineClamp={1}
                  >
                    {company.name}
                  </Text>
                </Box>
              </Grid>
            </TableRowCell>
          </IntersectionObserver>
          <TableRowCell>
            <div onClick={handleClick} data-cy-id="share-price">
              <Text typography="xSmall" isTruncated>
                {formatterCurrency(lastSharePrice, companyCurrencyISO)}
              </Text>
              <Text typography="tinyCondensed" color="whiteRGBA" opacity={0.5}>
                <CompanyIntrinsicDiscount
                  lastSharePrice={lastSharePrice}
                  npvPerShare={npvPerShare}
                  fairValue={analysis.fairValue}
                />
              </Text>
            </div>
          </TableRowCell>
          <TableRowCell>
            <div onClick={handleClick} data-cy-id="return-7d">
              <PositiveNegativeValue value={analysis.return7d}>
                {formatterPercentage(analysis.return7d)}
              </PositiveNegativeValue>
              <Text typography="tinyCondensed" color="whiteRGBA" opacity={0.5}>
                {formatCurrencyTruncatedWithSign(
                  analysis.return7dValue,
                  currencyISO
                )}
              </Text>
            </div>
          </TableRowCell>
          <TableRowCell>
            <div onClick={handleClick} data-cy-id="holding-roi">
              <PositiveNegativeValue value={analysis.roi}>
                {formatterPercentage(analysis.roi)}
              </PositiveNegativeValue>
              <Text typography="tinyCondensed" color="whiteRGBA" opacity={0.5}>
                {formatCurrencyTruncatedWithSign(
                  analysis.roiValue,
                  currencyISO
                )}
              </Text>
            </div>
          </TableRowCell>
          <TableRowCell>
            <div onClick={handleClick} data-cy-id="holding-value">
              <Text typography="xSmall" isTruncated>
                {formatCurrencyToScaleTruncated(analysis.value, currencyISO)}
              </Text>
              <Text typography="tinyCondensed" color="whiteRGBA" opacity={0.5}>
                {formatCurrencyToScaleTruncated(
                  analysis.capitalInvested,
                  currencyISO
                )}
              </Text>
            </div>
          </TableRowCell>
          <TableRowCell data-cy-id="weight-shares">
            <div onClick={handleClick}>
              <Text typography="xSmall" isTruncated>
                {formatterPercentage(analysis.allocation)}
              </Text>
              <Text typography="tinyCondensed" color="whiteRGBA" opacity={0.5}>
                {toSignificant(units, 2)}
              </Text>
            </div>
          </TableRowCell>
          <TableRowCell data-cy-id="avg-price">
            <div onClick={handleClick}>
              <Text typography="xSmall" isTruncated>
                {formatterCurrency(averagePurchasePrice, companyCurrencyISO)}
              </Text>
            </div>
          </TableRowCell>
          <TableRowCell colSpan={2}>
            <DeferRender>
              <MiniPriceChart
                period="1Y"
                companyId={company.id}
                currencyISO={companyCurrencyISO}
              />
            </DeferRender>
          </TableRowCell>
        </HoldingsTableRow>
      )}
    </Track>
  );
};
